/**
 * QuotaMeter — always-visible quota display for company chrome.
 *
 * J-C6: shows X / Y attempts used. Warning state at ≥ 90%.
 * Top-up CTA links to "Contact your SME for top-up" (Stripe deferred).
 *
 * Props-driven — the parent (Shell) fetches quota data and passes it down.
 * This avoids the component making its own fetches.
 */

import type { ReactElement } from 'react';

export interface QuotaMeterProps {
  /** Attempts consumed. */
  quotaUsed: number;
  /** Total attempts in the quota plan. */
  quotaTotal: number;
}

export function QuotaMeter({ quotaUsed, quotaTotal }: QuotaMeterProps): ReactElement {
  const ratio = quotaTotal > 0 ? quotaUsed / quotaTotal : 0;
  const isWarning = ratio >= 0.9;
  const remaining = Math.max(0, quotaTotal - quotaUsed);

  return (
    <div
      className={`flex items-center gap-3 rounded-md border px-3 py-2 font-sans text-small transition-colors duration-200 ${
        isWarning
          ? 'border-warning bg-warning-l text-warning'
          : 'border-line bg-card text-muted'
      }`}
      role="status"
      aria-label={`Quota: ${quotaUsed} of ${quotaTotal} attempts used, ${remaining} remaining`}
    >
      {/* Progress bar */}
      <div className="h-1.5 w-16 overflow-hidden rounded-pill bg-line" aria-hidden="true">
        <div
          className={`h-full rounded-pill transition-all duration-300 ${
            isWarning ? 'bg-warning' : 'bg-accent'
          }`}
          style={{ width: `${Math.min(100, ratio * 100)}%` }}
        />
      </div>

      <span className="whitespace-nowrap font-mono text-xs">
        {quotaUsed} / {quotaTotal} attempts used
      </span>

      {isWarning && (
        <span className="font-mono text-xs font-medium uppercase tracking-wide">
          Low quota
        </span>
      )}

      <span className="ml-auto whitespace-nowrap font-sans text-xs text-muted">
        Contact your SME for top-up
      </span>
    </div>
  );
}
